import classNames from 'classnames'
import React, { ReactElement, ReactNode } from 'react'
import Icon, { IconProps } from './Icon'
import SwitchButton, { SwitchButtonProps } from './SwitchButton'

export type AccessoryIconProps =
  | 'caret'
  | 'share-link'
  | 'copy'
  | SwitchButtonProps
interface TableRowProps {
  onClick?: () => void // 點擊範圍為整個 row
  compact?: boolean
  icon?: IconProps
  image?: React.ImgHTMLAttributes<HTMLImageElement>
  title?: string | ReactNode | React.ReactNode[] | null
  titleClassName?: string
  containerClassName?: string
  content?: string
  contentClassName?: string
  description?: string
  descriptionClassName?: string
  accessory?: ReactNode
  accessoryLabel?: string
  accessoryLabelClassName?: string
  accessoryType?: AccessoryIconProps
  onClickAccessory?: () => void // 點擊範圍為只在 Accessory 區塊
  divided?: boolean
  dividerContainerClassName?: string
}

function AccessoryIcon({
  accessoryType,
}: {
  accessoryType: AccessoryIconProps
}): ReactElement | null {
  switch (accessoryType) {
    case 'caret':
      return <Icon name="rightopen" />
    case 'share-link':
      return <Icon name="share-link" />
    case 'copy':
      return <Icon name="copy" />
    default:
      return null
  }
}

export default function TableRow({
  onClick,
  compact,
  icon,
  image,
  title,
  titleClassName,
  containerClassName,
  content,
  contentClassName,
  description,
  descriptionClassName,
  accessory,
  accessoryLabel,
  accessoryLabelClassName,
  accessoryType,
  onClickAccessory,
  divided,
  dividerContainerClassName,
}: TableRowProps) {
  return (
    <div
      className={classNames(
        'flex flex-col babu-table-container px-m',
        {
          'pb-s': !compact && !divided,
          'pt-m': !compact,
          'cursor-pointer active:bg-bg200': !!onClick,
        },
        containerClassName,
      )}
      onClick={onClick}>
      <div className="flex justify-between">
        <div className="flex items-center">
          {image ? (
            <img
              {...image}
              className={classNames(
                'w-16 h-16 object-cover rounded-2xl',
                image.className,
              )}
            />
          ) : icon ? (
            <Icon {...icon} />
          ) : null}
          <div className="mr-m">
            {typeof title === 'string' ? (
              <p
                className={classNames(
                  'babu-text-paragraph-bold',
                  titleClassName,
                )}>
                {title}
              </p>
            ) : title ? (
              title
            ) : null}
            {content ? (
              <p className={classNames('babu-text', contentClassName)}>
                {content}
              </p>
            ) : null}
            {description ? (
              <p
                className={classNames(
                  'babu-text-fineprint text-text500',
                  descriptionClassName,
                )}>
                {description}
              </p>
            ) : null}
          </div>
        </div>
        {accessory ? (
          accessory
        ) : accessoryLabel || accessoryType ? (
          <div
            className="flex items-center accessory"
            onClick={onClickAccessory}>
            {accessoryLabel && (
              <p
                className={classNames(
                  'accessory-label mr-eXxs',
                  accessoryLabelClassName,
                )}>
                {accessoryLabel}
              </p>
            )}
            {accessoryType ? (
              typeof accessoryType === 'string' ? (
                <div className="accessory-type cursor-pointer">
                  <AccessoryIcon accessoryType={accessoryType} />
                </div>
              ) : (
                <SwitchButton {...accessoryType} />
              )
            ) : null}
          </div>
        ) : null}
      </div>
      {divided && (
        <div
          className={classNames(
            'w-full mt-s',
            // 因為 classname 的值不能設定成變數，所以此 padding left 是抓 Icon 的預設大小
            // 如果遇到比預設大的情況，再用傳入 dividerContainerClassName 的方式加上
            { 'pl-[3.4625rem]': icon },
            { dividerContainerClassName },
          )}>
          <hr className="w-full border-0 border-t" />
        </div>
      )}
    </div>
  )
}
